// Întrebările pentru pagina „Întrebări” (admin): încărcarea pe set, forma din editor,
// căutarea, verificarea și salvarea.
//
// Un singur tabel de întrebări pentru examen (biletele B…) și teorie (P1–P24); le
// deosebește tipul biletului. Răspunsul corect e numărat diferit pe server: la examen
// de la 1, la teorie de la 0 (exams.service.ts compară direct cu ce trimite elevul).
// În editor e mereu de la 0 (`correct`); la salvare se pune înapoi după set.

import {
   buildExamTicketsExportBundle,
   downloadJsonExport,
   filterPracticeTheoryTickets,
   getExamTickets,
   loadAllPracticeTickets,
   updateExamQuestion,
} from "../../../api/examAdminService";
import {
   normalizeExamQuestionRecord,
   stringifyLocalizedAnswer,
} from "../../../utils/examQuestionFormat";
import {
   extractTicketNumber,
   resolvePracticeTicketNumber,
} from "../../../utils/examTicketsOfficialImport";
import { rewriteImageUrl } from "../../Utils/rewriteImageUrl";

export const SETS = {
   EXAM: { key: "EXAM", label: "Examen", prefix: "B", hint: false },
   PRACTICE: { key: "PRACTICE", label: "Teorie P1–P24", prefix: "P", hint: true },
};

export const MIN_ANSWERS = 2;
export const MAX_ANSWERS = 6;

export const NO_IMAGE_URL = "https://auto-test.online/static/custom/img/noimage.jpg";

// ── încărcarea ───────────────────────────────────────────────────────────────

async function loadExamTicketsRaw() {
   const all = [];
   for (let page = 1; page < 100; page += 1) {
      const res = await getExamTickets({ page, limit: 100, type: "EXAM" });
      all.push(...(res?.items || []));
      const totalPages = Number(res?.pagination?.totalPages);
      if (Number.isInteger(totalPages) && totalPages > 0 ? page >= totalPages : (res?.items || []).length < 100) break;
   }
   return all;
}

/** Biletele unui set, cu întrebările lor (lista de bilete le aduce întregi). */
export async function loadTickets(set) {
   const raw = set === "EXAM" ? await loadExamTicketsRaw() : filterPracticeTheoryTickets(await loadAllPracticeTickets());
   const seen = new Set();
   return raw
      .map((t) => normalizeTicket(t, set))
      .filter((t) => t && !seen.has(t.id) && seen.add(t.id))
      .sort((a, b) => (a.no ?? 9999) - (b.no ?? 9999) || a.id - b.id);
}

function normalizeTicket(raw, set) {
   const id = Number(raw?.id);
   if (!Number.isInteger(id)) return null;
   const name = String(raw?.name ?? "").trim();
   const no = set === "PRACTICE" ? resolvePracticeTicketNumber(raw) : extractTicketNumber(name);
   const links = Array.isArray(raw?.questions) ? raw.questions : [];
   const questions = links
      .map((link, i) => normalizeQuestion(link?.question ?? link, set, Number(link?.order) || i + 1))
      .filter(Boolean)
      .sort((a, b) => a.order - b.order);
   return { id, name, set, no, label: `${SETS[set].prefix}${no ?? id}`, questions };
}

/** Întrebarea în forma editorului; `correct` e de la 0 (-1 = lipsește / greșit). */
export function normalizeQuestion(raw, set, order = 1) {
   const base = normalizeExamQuestionRecord(raw, order - 1);
   if (!base) return null;
   const n = Number(raw?.correctAnswer ?? raw?.correct_answer);
   const idx = set === "EXAM" ? n - 1 : n;
   const valid = Number.isInteger(idx) && idx >= 0 && idx < base.answers.length;
   return {
      id: base.id,
      order,
      textRo: base.textRo || "",
      textRu: base.textRu || "",
      answers: base.answers.map((a) => ({ ro: a.ro || "", ru: a.ru || "" })),
      correct: valid ? idx : -1,
      image: base.image || "",
      hintRo: base.hintRo || "",
      hintRu: base.hintRu || "",
      categoryId: base.categoryId ?? null,
   };
}

// ── imaginea, ca la elev ─────────────────────────────────────────────────────

// examenul (Exam.jsx): /EXAMEN_INSTRUIRE_AUTO/images/B12/image3.jpeg → /images/B12/Image3.jpeg
function examImagePath(raw) {
   const src = String(raw || "").trim();
   if (!src) return null;
   const noHost = src.replace(/^https?:\/\/[^/]+/i, "");
   const withSlash = noHost.startsWith("/") ? noHost : `/${noHost}`;
   const compact = withSlash.replace(/^\/EXAMEN_INSTRUIRE_AUTO(?=\/)/i, "").replace(/\/{2,}/g, "/");
   if (!compact) return null;
   const withImages = /^\/images\//i.test(compact) ? compact : `/images${compact}`;
   return withImages.replace(/\/image(\d+)\.(?:jpe?g|png|webp)$/i, "/Image$1.jpeg");
}

/** Adresa imaginii cum o vede elevul: examenul (Exam.jsx) sau teoria (Practice.jsx). */
export function imageUrl(image, set) {
   if (!String(image || "").trim()) return null;
   return set === "EXAM" ? examImagePath(image) : rewriteImageUrl(image);
}

// ── căutarea ─────────────────────────────────────────────────────────────────

export function fold(value) {
   return String(value ?? "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, " ")
      .replace(/\s+/g, " ")
      .trim();
}

/** Indexul căutării: biletul (B12, „bilet 12”), id-ul, textul și răspunsurile, RO și RU. */
export function buildSearchIndex(tickets) {
   const out = [];
   tickets.forEach((ticket) => {
      ticket.questions.forEach((q) => {
         out.push({
            ticketId: ticket.id,
            questionId: q.id,
            hay: fold(
               [
                  ticket.label,
                  `bilet ${ticket.no ?? ""}`,
                  ticket.name,
                  `#${q.id}`,
                  q.id,
                  q.textRo,
                  q.textRu,
                  ...q.answers.flatMap((a) => [a.ro, a.ru]),
               ].join(" "),
            ),
         });
      });
   });
   return out;
}

/** Toate cuvintele din căutare trebuie să se găsească (cel mult `limit` rezultate). */
export function search(index, query, limit = 60) {
   const tokens = fold(query).split(" ").filter(Boolean);
   if (!tokens.length) return [];
   const hits = [];
   for (const entry of index) {
      if (tokens.every((t) => entry.hay.includes(t))) {
         hits.push(entry);
         if (hits.length >= limit) break;
      }
   }
   return hits;
}

// ── verificarea (aceleași reguli ca în Corectare examen) ────────────────────

export function verifyQuestion(q) {
   const issues = [];
   if (!q.textRo.trim()) issues.push("Fără text RO");
   if (!q.textRu.trim()) issues.push("Fără text RU");
   if (q.answers.length < MIN_ANSWERS) issues.push(`Mai puțin de ${MIN_ANSWERS} răspunsuri`);
   q.answers.forEach((a, i) => {
      const ro = a.ro.trim();
      const ru = a.ru.trim();
      if (!ro && !ru) issues.push(`Răspunsul ${i + 1} e gol`);
      else if (!ro) issues.push(`Răspunsul ${i + 1}: fără RO`);
      else if (!ru) issues.push(`Răspunsul ${i + 1}: fără RU`);
   });
   if (q.correct < 0 || q.correct >= q.answers.length) issues.push("Răspunsul corect lipsește");
   else if (!q.answers[q.correct].ro.trim() && !q.answers[q.correct].ru.trim())
      issues.push("Răspunsul corect e gol");
   return issues;
}

// ── salvarea ─────────────────────────────────────────────────────────────────

/** Ce trimitem la PUT /exams/questions/:id (doar ce s-a schimbat la imagine, categorie, explicație). */
export function buildSavePayload(draft, original, set) {
   const textRo = draft.textRo.trim();
   const textRu = draft.textRu.trim();
   if (!textRo && !textRu) throw new Error("Întrebarea trebuie să aibă text în cel puțin o limbă.");
   const answers = draft.answers.map((a) => ({ ro: a.ro.trim(), ru: a.ru.trim() }));
   if (answers.length < MIN_ANSWERS) throw new Error(`Sunt necesare cel puțin ${MIN_ANSWERS} răspunsuri.`);
   if (answers.some((a) => !a.ro && !a.ru)) throw new Error("Completează toate răspunsurile (sau șterge-le pe cele goale).");
   if (draft.correct < 0 || draft.correct >= answers.length) throw new Error("Alege răspunsul corect.");

   const payload = {
      text: JSON.stringify({ ro: textRo, ru: textRu }),
      answers: answers.map(stringifyLocalizedAnswer),
      correctAnswer: set === "EXAM" ? draft.correct + 1 : draft.correct,
   };
   if (draft.image.trim() !== original.image.trim()) payload.image = draft.image.trim();
   if (draft.categoryId && Number(draft.categoryId) !== Number(original.categoryId)) {
      payload.categoryId = Number(draft.categoryId);
   }
   if (draft.hintRo.trim() !== original.hintRo.trim() || draft.hintRu.trim() !== original.hintRu.trim()) {
      payload.hint = JSON.stringify({ ro: draft.hintRo.trim(), ru: draft.hintRu.trim() });
   }
   return payload;
}

export async function saveQuestion(draft, original, set) {
   const payload = buildSavePayload(draft, original, set);
   await updateExamQuestion(draft.id, payload);
   return {
      ...draft,
      textRo: draft.textRo.trim(),
      textRu: draft.textRu.trim(),
      answers: draft.answers.map((a) => ({ ro: a.ro.trim(), ru: a.ru.trim() })),
      image: draft.image.trim(),
      hintRo: draft.hintRo.trim(),
      hintRu: draft.hintRu.trim(),
      // serverul nu scoate o întrebare dintr-o categorie: rămâne cea veche
      categoryId: draft.categoryId ? Number(draft.categoryId) : original.categoryId,
   };
}

export function isDirty(draft, original) {
   if (!draft || !original) return false;
   return JSON.stringify(draft) !== JSON.stringify(original);
}

// ── exportul (ca în paginile de corectare) ───────────────────────────────────

export async function exportSet(set) {
   const bundle = await buildExamTicketsExportBundle({ ticketType: set });
   const day = new Date().toISOString().slice(0, 10);
   downloadJsonExport(bundle, set === "EXAM" ? `bilete-exam-export-${day}.json` : `bilete-teorie-P1-P24-${day}.json`);
}
